import type {
  DerivedKey,
  EncryptedPayload,
  KeyDerivationConfiguration,
  KeyDerivationParameters,
  PrivateVault,
} from '../../application/models';
import type { BackupCipher, CryptoPort } from '../../application/ports';
import { Aes256Gcm, AES_GCM_IV_LENGTH_BYTES } from './aes-256-gcm';
import { Argon2idKdf } from './argon2id-kdf';
import { base64ToBytes, bytesToBase64 } from './base64';
import { PrivateVaultCodec, reviveValueObject } from './private-vault-codec';

class WebCryptoDerivedKey implements DerivedKey {
  readonly opaqueHandle = 'webcrypto:aes-256-gcm';

  constructor(
    readonly keyDerivation: KeyDerivationParameters,
    readonly cryptographicKey: CryptoKey,
  ) {}
}

const ENCRYPTED_BLOB_VERSION = 1;
const ENCRYPTED_BLOB_ALGORITHM = 'AES-256-GCM';
const associatedDataEncoder = new TextEncoder();
const documentDecoder = new TextDecoder('utf-8', { fatal: true });

/**
 * What a sealed document is bound to besides its key: what it is, how its key was derived, and
 * its nonce. Altering any of them in the file makes opening fail.
 */
const documentAssociatedData = (
  context: string,
  parameters: KeyDerivationParameters,
  algorithm: string,
  initializationVector: string,
): Uint8Array<ArrayBuffer> =>
  associatedDataEncoder.encode(
    JSON.stringify({
      context,
      kdfParams: {
        algorithm: parameters.algorithm,
        memoryCostKiB: parameters.memoryCostKiB,
        iterations: parameters.iterations,
        parallelism: parameters.parallelism,
        keyLengthBytes: parameters.keyLengthBytes,
        saltLengthBytes: parameters.saltLengthBytes,
        salt: parameters.salt,
      },
      algorithm,
      initializationVector,
    }),
  );

const serializeEnvelopeAssociatedData = (
  parameters: KeyDerivationParameters,
  algorithm: string,
  initializationVector: string,
): Uint8Array<ArrayBuffer> =>
  associatedDataEncoder.encode(
    JSON.stringify({
      version: ENCRYPTED_BLOB_VERSION,
      kdfParams: {
        algorithm: parameters.algorithm,
        memoryCostKiB: parameters.memoryCostKiB,
        iterations: parameters.iterations,
        parallelism: parameters.parallelism,
        keyLengthBytes: parameters.keyLengthBytes,
        saltLengthBytes: parameters.saltLengthBytes,
        salt: parameters.salt,
      },
      algorithm,
      initializationVector,
    }),
  );

export class CryptoDecryptionError extends Error {
  constructor() {
    super('Encrypted vault authentication failed');
    this.name = 'CryptoDecryptionError';
  }
}

const unwrapKey = (key: DerivedKey): CryptoKey => {
  if (!(key instanceof WebCryptoDerivedKey)) {
    throw new TypeError('Derived key was not created by WebCryptoAdapter');
  }
  return key.cryptographicKey;
};

export class WebCryptoAdapter implements CryptoPort, BackupCipher {
  private readonly aes: Aes256Gcm;

  constructor(
    private readonly kdf: Argon2idKdf,
    private readonly cryptography: Pick<Crypto, 'getRandomValues' | 'subtle'> = globalThis.crypto,
    private readonly codec: Pick<PrivateVaultCodec, 'encode' | 'decode'> = new PrivateVaultCodec(),
  ) {
    this.aes = new Aes256Gcm(cryptography.subtle);
  }

  async deriveKey(
    passphrase: string,
    parameters: KeyDerivationConfiguration | KeyDerivationParameters,
  ): Promise<DerivedKey> {
    // Platform limitation: a passphrase is an immutable JavaScript string and cannot be zeroed.
    // Keep its lifetime short and discard it on vault lock, as documented in SAS section 7.
    const material = await this.kdf.deriveKey(passphrase, parameters);
    try {
      const cryptographicKey = await this.aes.importKey(material.bytes);
      return new WebCryptoDerivedKey(material.parameters, cryptographicKey);
    } finally {
      material.bytes.fill(0);
    }
  }

  async encrypt(vault: PrivateVault, key: DerivedKey): Promise<EncryptedPayload> {
    const iv = new Uint8Array(AES_GCM_IV_LENGTH_BYTES);
    this.cryptography.getRandomValues(iv);
    const initializationVector = bytesToBase64(iv);
    const associatedData = serializeEnvelopeAssociatedData(
      key.keyDerivation,
      ENCRYPTED_BLOB_ALGORITHM,
      initializationVector,
    );
    const plaintext = this.codec.encode(vault);
    try {
      const ciphertext = await this.aes.encrypt(plaintext, unwrapKey(key), iv, associatedData);
      return Object.freeze({
        algorithm: ENCRYPTED_BLOB_ALGORITHM,
        initializationVector,
        ciphertext: bytesToBase64(ciphertext),
      });
    } finally {
      plaintext.fill(0);
    }
  }

  async decrypt(payload: EncryptedPayload, key: DerivedKey): Promise<PrivateVault> {
    try {
      const associatedData = serializeEnvelopeAssociatedData(
        key.keyDerivation,
        payload.algorithm,
        payload.initializationVector,
      );
      const plaintext = await this.aes.decrypt(
        base64ToBytes(payload.ciphertext),
        unwrapKey(key),
        base64ToBytes(payload.initializationVector),
        associatedData,
      );
      try {
        return this.codec.decode(plaintext);
      } finally {
        plaintext.fill(0);
      }
    } catch {
      throw new CryptoDecryptionError();
    }
  }

  async seal(document: unknown, key: DerivedKey, context: string): Promise<EncryptedPayload> {
    const iv = new Uint8Array(AES_GCM_IV_LENGTH_BYTES);
    this.cryptography.getRandomValues(iv);
    const initializationVector = bytesToBase64(iv);
    const associatedData = documentAssociatedData(
      context,
      key.keyDerivation,
      ENCRYPTED_BLOB_ALGORITHM,
      initializationVector,
    );
    const plaintext = associatedDataEncoder.encode(JSON.stringify(document));
    try {
      const ciphertext = await this.aes.encrypt(plaintext, unwrapKey(key), iv, associatedData);
      return Object.freeze({
        algorithm: ENCRYPTED_BLOB_ALGORITHM,
        initializationVector,
        ciphertext: bytesToBase64(ciphertext),
      });
    } finally {
      plaintext.fill(0);
    }
  }

  async open(payload: EncryptedPayload, key: DerivedKey, context: string): Promise<unknown> {
    let plaintext: Uint8Array;
    try {
      plaintext = await this.aes.decrypt(
        base64ToBytes(payload.ciphertext),
        unwrapKey(key),
        base64ToBytes(payload.initializationVector),
        documentAssociatedData(
          context,
          key.keyDerivation,
          payload.algorithm,
          payload.initializationVector,
        ),
      );
    } catch {
      throw new CryptoDecryptionError();
    }
    try {
      return JSON.parse(documentDecoder.decode(plaintext), reviveValueObject);
    } finally {
      plaintext.fill(0);
    }
  }
}
